import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Form, Row, Col, Button, Card, Alert, Modal, ProgressBar
} from 'react-bootstrap';
import { FaCheck, FaTimes, FaPlus } from 'react-icons/fa';
import axios from 'axios';

const UaeSecurityEncryptionTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities,logToAccessLogs }) => {
  const [securityMeasures, setSecurityMeasures] = useState([]);
  const [encryptionStatus, setEncryptionStatus] = useState({});
  const [accessControls, setAccessControls] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newMeasure, setNewMeasure] = useState({
    category: '',
    measure: '',
    implementation: 'Implemented',
    description: '',
    responsibleParty: '',
    reviewDate: '',
    pdplArticle: ''
  });
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [newAccessControl, setNewAccessControl] = useState({
    type: '',
    description: '',
    status: 'Active',
    coverage: 0
  });

  const handleSaveAccessControl = async () => {
    try {
      await axios.post('https://9nbxhs0qlb.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/create', newAccessControl, { headers });
      await logActivity(`Added access control: ${newAccessControl.type}`, "success");
      await logActivity(`Added access control: ${newAccessControl.type}`, 'success');
      logToAccessLogs?.({
        action:      'Access Control Added',
        severity:    'success',
        description: `Added UAE access control: ${newAccessControl.type}`,});
      showNotification('Access control added successfully!');
      fetchSecurityData();
      resetAccessForm();
    } catch (error) {
      console.error('Error saving access control:', error);
      showNotification('Error saving access control', 'danger');
    }
  };

  const resetAccessForm = () => {
    setNewAccessControl({
      type: '',
      description: '',
      status: 'Active',
      coverage: 0
    });
    setShowAccessModal(false);
  };

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const SECURITY_CATEGORIES = [
    'Technical Safeguards',
    'Administrative Controls',
    'Physical Security',
    'Access Management',
    'Data Encryption',
    'Network Protection',
    'Incident Response',
    'Personnel Training',
    'Third-Party Management',
    'Business Continuity',
    'Data Loss Prevention',
    'Identity Management'
  ];

  const UAE_PDPL_ARTICLES = [
    'Art. 15 - Security of personal data',
    'Art. 16 - Data protection by design and default',
    'Art. 18 - Data processor obligations',
    'Art. 19 - Data breach notification requirements',
    'Art. 20 - Risk assessment obligations',
    'Art. 21 - Technical and organizational measures',
    'Art. 22 - Access control requirements'
  ];

useEffect(() => {
  // Save encryption status SEPARATELY — don't let it block the GETs
  axios.post("https://c9ycldh03k.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/encryption-status/save", {
    dataAtRestEncrypted: true,
    atRestAlgorithm: "AES-256",
    dataInTransitEncrypted: true,
    inTransitProtocol: "TLS 1.3",
    keyManagement: "Hardware Security Module (HSM)",
    complianceStandard: "UAE PDPL"
  }, { headers }).catch(e => console.warn("Encryption save failed:", e.message));

  fetchSecurityData();
}, []);

  const fetchSecurityData = async () => {
    try {
      // Save encryption status with UAE-compliant standards
      

      // Fetch UAE-specific security data
      const [measuresRes, encryptionRes, accessRes] = await Promise.all([
        axios.get('https://xicm1mmtob.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/security-measures/list', { headers }),
        axios.get('https://t0r517yql1.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/encryption-status', { headers }),
        axios.get('https://6b9o8hphx5.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/list', { headers })
      ]);

      setSecurityMeasures(measuresRes.data);
      setEncryptionStatus(encryptionRes.data);
      setAccessControls(accessRes.data);
      
      calculateSecurityScore(measuresRes.data, encryptionRes.data, accessRes.data);
    } catch (error) {
      console.error('Error fetching UAE security data:', error);
    }
  };

  const calculateSecurityScore = (measures, encryption, access) => {
    let score = 0;
    
    // Technical Measures (40% of score) - UAE PDPL Article 15 compliance
    const implementedMeasures = measures.filter(m => m.implementation === 'Implemented').length;
    const totalMeasures = measures.length || 1;
    const technicalScore = (implementedMeasures / totalMeasures) * 40;
    
    // Encryption Coverage (35% of score) - UAE PDPL Article 21 requirements
    const encryptionCoverage = encryption.dataAtRestEncrypted && encryption.dataInTransitEncrypted ? 35 : 
                              (encryption.dataAtRestEncrypted || encryption.dataInTransitEncrypted) ? 17.5 : 0;
    
    // Access Controls (25% of score) - UAE PDPL Article 22 compliance
    const rbacImplemented = access.some(a => a.type === 'Role-Based Access Control');
    const mfaImplemented = access.some(a => a.type === 'Multi-Factor Authentication');
    const privilegedAccessImplemented = access.some(a => a.type === 'Privileged Access Management');
    
    let accessScore = 0;
    if (rbacImplemented) accessScore += 8.5;
    if (mfaImplemented) accessScore += 8.5;
    if (privilegedAccessImplemented) accessScore += 8;
    
    score = Math.round(technicalScore + encryptionCoverage + accessScore);
    onScoreUpdate(Math.min(100, Math.max(0, score)));
  };

  const handleSaveMeasure = async () => {
    try {
      await axios.post('https://4go8zhf1ie.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/security-measures/create', newMeasure, { headers });
      await logActivity(`Added UAE security measure: ${newMeasure.measure}`, "success");
      logToAccessLogs?.({
        action:      'Security Entry Created',
        severity:    'success',
        description: `Added UAE security measure: ${newMeasure.measure} (${newMeasure.category})`,});
      showNotification('Security measure added successfully!');
      fetchSecurityData();
      resetForm();
    } catch (error) {
      console.error('Error saving UAE security measure:', error);
      showNotification('Error saving security measure', 'danger');
    }
  };

  const resetForm = () => {
    setNewMeasure({
      category: '',
      measure: '',
      implementation: 'Implemented',
      description: '',
      responsibleParty: '',
      reviewDate: '',
      pdplArticle: ''
    });
    setShowModal(false);
  };

  const logActivity = async (message, badge = "info") => {  
    try {
      const payload = {
        action: badge,
        module: "UAE Security/Encryption",
        details: message
      };
      
      const response = await axios.post(
        "https://6x7bayihnj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/save", 
        payload,     
        { headers }  
      );
      
      if (refreshActivities) {
        await refreshActivities();
      }
      
    } catch (err) {
      console.error('❌ Error in UAE security logActivity:', err);
      
      if (err.response) {
        console.error('🔥 Server Error Details:');
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }
      
      console.warn("Failed to log UAE security activity:", err.message);
    }
  };

  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  
    return (
      <>
        <div className="border rounded">
    <div
      className="d-flex justify-content-between align-items-center p-3" 
      style={{ cursor: "pointer" }}
      onClick={() =>
        setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
      }
    >
      <h6>UAE Security Measures Overview</h6>
      <span>{activeAccordionKey === "0" ? "▲" : "▼"}</span>
    </div>
  
    {activeAccordionKey === "0" && (
      <div className="p-3 border-top">
            <Alert variant="info" className="mb-3">
              <strong>UAE Personal Data Protection Law Compliance:</strong> All security measures must comply with 
              Federal Decree-Law No. 45 of 2021, specifically Articles 15, 16, 18, and 21 regarding technical and 
              organizational security measures.
            </Alert>

            <Row className="mb-3">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-primary">{securityMeasures.filter(m => m.implementation === 'Implemented').length}</h4>
                    <small>Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-warning">{securityMeasures.filter(m => m.implementation === 'In Progress').length}</h4>
                    <small>In Progress</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4 className="text-danger">{securityMeasures.filter(m => m.implementation === 'Not Implemented').length}</h4>
                    <small>Not Implemented</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h4>{securityMeasures.length}</h4>
                    <small>Total Measures</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Button variant="primary" className="mb-3" onClick={() => setShowModal(true)}>
              <FaPlus /> Add Security Measure
            </Button>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Security Measure</th>
                  <th>Implementation Status</th>
                  <th>Responsible Party</th>
                  <th>UAE PDPL Article</th>
                  <th>Review Date</th>
                </tr>
              </thead>
              <tbody>
                {securityMeasures.map((measure, index) => (
                  <tr key={index}>
                    <td>{measure.category}</td>
                    <td>{measure.measure}</td>
                    <td>
                      <Badge bg={
                        measure.implementation === 'Implemented' ? 'success' :
                        measure.implementation === 'In Progress' ? 'warning' : 'danger'
                      }>
                        {measure.implementation}
                      </Badge>
                    </td>
                    <td>{measure.responsibleParty}</td>
                    <td>{measure.pdplArticle}</td>
                    <td>{measure.reviewDate}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
    )}

        <div
      className="d-flex justify-content-between align-items-center p-3" 
      style={{ cursor: "pointer" }}
      onClick={() =>
        setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
      }
    >
      <h6>Personal Data Encryption Status</h6>
      <span>{activeAccordionKey === "1" ? "▲" : "▼"}</span>
    </div>
  
    {activeAccordionKey === "1" && (
      <div className="p-3 border-top">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Personal Data at Rest Encryption</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataAtRestEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataAtRestEncrypted ? 'Enabled' : 'Not Enabled'}</span>
                    </div>
                    <small className="text-muted">Algorithm: {encryptionStatus.atRestAlgorithm || 'Not specified'}</small>
                    <br />
                    <small className="text-muted">Key Management: {encryptionStatus.keyManagement || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Personal Data in Transit Encryption</Card.Title>
                    <div className="d-flex align-items-center">
                      {encryptionStatus.dataInTransitEncrypted ? 
                        <FaCheck className="text-success me-2" /> : 
                        <FaTimes className="text-danger me-2" />
                      }
                      <span>{encryptionStatus.dataInTransitEncrypted ? 'Enabled' : 'Not Enabled'}</span>
                    </div>
                    <small className="text-muted">Protocol: {encryptionStatus.inTransitProtocol || 'Not specified'}</small>
                    <br />
                    <small className="text-muted">Standard: {encryptionStatus.complianceStandard || 'Not specified'}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Alert variant="info" className="mt-3">
              <strong>UAE PDPL Requirement:</strong> Article 15 and 21 require appropriate technical measures including 
              encryption of personal data to ensure security appropriate to the risk. The UAE requires strong encryption 
              standards including AES-256 for data at rest and TLS 1.3 for data in transit.
            </Alert>
          </div>
    )}

         <div
      className="d-flex justify-content-between align-items-center p-3" 
      style={{ cursor: "pointer" }}
      onClick={() =>
        setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
      }
    >
      <h6>Access Controls & Identity Management</h6>
      <span>{activeAccordionKey === "2" ? "▲" : "▼"}</span>
    </div>
  
    {activeAccordionKey === "2" && (
      <div className="p-3 border-top">
            <Alert variant="warning" className="mb-3">
              <strong>UAE PDPL Article 22:</strong> Controllers must implement appropriate access control measures 
              to prevent unauthorized access to personal data and ensure only authorized personnel can access 
              personal data as necessary for their duties.
            </Alert>

            <Button variant="primary" className="mb-3" onClick={() => setShowAccessModal(true)}>
              <FaPlus /> Add Access Control
            </Button>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Control Type</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {accessControls.map((control, index) => (
                  <tr key={index}>
                    <td>{control.type}</td>
                    <td>{control.description}</td>
                    <td>
                      <Badge bg={control.status === 'Active' ? 'success' : 'warning'}>
                        {control.status}
                      </Badge>
                    </td>
                    <td>
                      <ProgressBar 
                        now={control.coverage} 
                        label={`${control.coverage}%`}
                        variant={control.coverage >= 80 ? 'success' : control.coverage >= 50 ? 'warning' : 'danger'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
    )}
      </div>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add UAE Security Measure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={newMeasure.category}
                    onChange={(e) => setNewMeasure({...newMeasure, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    {SECURITY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Implementation Status</Form.Label>
                  <Form.Select
                    value={newMeasure.implementation}
                    onChange={(e) => setNewMeasure({...newMeasure, implementation: e.target.value})}
                  >
                    <option value="Implemented">Implemented</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Not Implemented">Not Implemented</option>
                    <option value="Under Review">Under Review</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Security Measure</Form.Label>
              <Form.Control
                type="text"
                value={newMeasure.measure}
                onChange={(e) => setNewMeasure({...newMeasure, measure: e.target.value})}
                placeholder="e.g., Multi-factor authentication for admin access to personal data"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newMeasure.description}
                onChange={(e) => setNewMeasure({...newMeasure, description: e.target.value})}
                placeholder="Describe how this measure protects personal data under UAE PDPL"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsible Party</Form.Label>
                  <Form.Control
                    type="text"
                    value={newMeasure.responsibleParty}
                    onChange={(e) => setNewMeasure({...newMeasure, responsibleParty: e.target.value})}
                    placeholder="Department or individual responsible"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Review Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newMeasure.reviewDate}
                    onChange={(e) => setNewMeasure({...newMeasure, reviewDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Related UAE PDPL Article</Form.Label>
              <Form.Select
                value={newMeasure.pdplArticle}
                onChange={(e) => setNewMeasure({...newMeasure, pdplArticle: e.target.value})}
              >
                <option value="">Select Article</option>
                {UAE_PDPL_ARTICLES.map(article => (
                  <option key={article} value={article}>{article}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveMeasure}>Save Security Measure</Button>
        </Modal.Footer>
      </Modal>

      {/* Access Control Modal */}
      <Modal show={showAccessModal} onHide={resetAccessForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add UAE Access Control</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Control Type</Form.Label>
                  <Form.Select
                    value={newAccessControl.type}
                    onChange={(e) => setNewAccessControl({...newAccessControl, type: e.target.value})}
                  >
                    <option value="">Select Control Type</option>
                    <option value="Role-Based Access Control">Role-Based Access Control</option>
                    <option value="Multi-Factor Authentication">Multi-Factor Authentication</option>
                    <option value="Privileged Access Management">Privileged Access Management</option>
                    <option value="Single Sign-On">Single Sign-On</option>
                    <option value="Identity Governance">Identity Governance</option>
                    <option value="Biometric Access Control">Biometric Access Control</option>
                    <option value="Data Loss Prevention">Data Loss Prevention</option>
                    <option value="Audit Logging">Audit Logging</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={newAccessControl.status}
                    onChange={(e) => setNewAccessControl({...newAccessControl, status: e.target.value})}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                    <option value="Under Review">Under Review</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newAccessControl.description}
                onChange={(e) => setNewAccessControl({...newAccessControl, description: e.target.value})}
                placeholder="Describe the access control implementation and its role in protecting personal data under UAE PDPL"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Coverage Percentage</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={newAccessControl.coverage}
                onChange={(e) => setNewAccessControl({...newAccessControl, coverage: parseInt(e.target.value) || 0})}
                placeholder="0-100"
              />
              <Form.Text className="text-muted">
                Percentage of personal data processing activities covered by this access control
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetAccessForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveAccessControl}>Save Access Control</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UaeSecurityEncryptionTab;
